// src/hooks/useScrollReveal.js
// 3.39.0: sections further down a page fade up gently as they scroll into
// view. Only sections below the fold when they appear get it (what you see
// first is never hidden), sections that load later are picked up too, and
// without IntersectionObserver (or with reduced motion) nothing changes.
import { useEffect } from 'react';

// root: the page's element (a state set by a callback ref, so this runs
// again once the page is on screen)
export default function useScrollReveal(root, key) {
  useEffect(() => {
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        e.target.classList.add('reveal-in');
        e.target.classList.remove('reveal-pending');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0.05 });
    const seen = new WeakSet();
    const take = el => {
      if (seen.has(el)) return;
      seen.add(el);
      if (el.getBoundingClientRect().top < window.innerHeight - 40) return;   // already on screen
      el.classList.add('reveal-pending');
      io.observe(el);
    };
    const scan = () => root.querySelectorAll('section').forEach(take);
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, [root, key]);
}
